/**
 * Timbrica — OrtEpVerify: an ONNX Runtime execution provider earns its verdict
 * by DOING the work, not by existing.
 *
 * Why this file exists (bug report 2026-09-09 09:48, /video-fps, Nothing
 * Phone 2 / Adreno 730 / Yandex Browser 26.8 = Chromium 150): the RIFE model
 * ran on the WebGPU provider, ORT reported success, the run took 8.5 minutes,
 * and every generated frame came out cut into vertical stripes — a 16-column
 * period with 8 of every 16 columns holding garbage (measured on the attached
 * frame: autocorrelation peaks at 16/32/48, the dark phases flat and
 * uncorrelated with their neighbours). No error, no telemetry, «Скачать видео».
 *
 * The probe we had (`RifeCore.probe`, `AIUpscaleCore.probe`) asks a trivial
 * compute shader for the number 3.0. That validates the API, not the kernels
 * the model actually dispatches — and on that phone it answered «ok». The same
 * blind spot exists in every ORT-WebGPU consumer of this site. This module is
 * the shape of the fix the codebase already uses elsewhere (DecoderVerify,
 * FRLandmarks): try the real thing, check the answer, step down a rung when it
 * is wrong, remember the rejection, and tell telemetry which adapter lied.
 *
 * Contract:
 *
 *   OrtEpVerify.ladder({
 *       providers: ['webgpu', 'wasm'],        // preference order
 *       create:    async (provider) => session,
 *       verify:    async (session, provider, round) => ({ ok, psnr, sig, ms }),
 *       release:   (session) => void,          // optional
 *       cacheKey:  'ort:1.24.3|model:76e4cef9|qualcomm/adreno-7xx|chrome150',
 *       repeat:    2,                          // rounds for non-CPU rungs
 *       onVerdict: (v) => void                 // optional, one call per rung
 *   }) → { session, provider, verdict, verdicts }
 *
 * The CPU rung ('wasm') is the reference: if it fails the consumer's own test
 * the test is what is wrong, so the ladder still returns that session, marked
 * `unverified: true`, rather than refusing to run at all.
 *
 * Pure helpers (unit-tested in tests/js/ort-ep-verify.test.mjs):
 *   psnr(a, b)                 — dB over RGB of two ImageData-like objects
 *   stripeSignature(a, b)      — { axis, period, strength, badFrac, meanErr }
 *   syntheticFrame(w, h)       — deterministic textured frame, alpha 255
 *   isFlat(img)                — a frame that cannot witness anything
 *   verdictKey(parts)          — join for the rejection cache
 *   rejections: isRejected / remember / forget (localStorage, 30-day TTL)
 *   adapterIdentity()          — { vendor, architecture, device, description, fallback }
 */
(function (global) {
    'use strict';

    var STORE_KEY = 'tmb_ort_ep_rejections';
    var TTL_MS = 30 * 24 * 3600 * 1000;
    var BAD_COL_ERR = 0.08;      // mean abs error (0..1) that marks a column/row as wrong

    function pxOf(img) { return img && img.data ? img.data : img; }

    // PSNR over RGB (alpha ignored), peak 1.0, capped at 99 dB for identical input.
    function psnr(a, b) {
        var x = pxOf(a), y = pxOf(b);
        if (!x || !y || x.length !== y.length) return 0;
        var se = 0, n = 0;
        for (var i = 0; i < x.length; i += 4) {
            var d0 = (x[i] - y[i]) / 255, d1 = (x[i + 1] - y[i + 1]) / 255, d2 = (x[i + 2] - y[i + 2]) / 255;
            se += d0 * d0 + d1 * d1 + d2 * d2; n += 3;
        }
        var mse = se / (n || 1);
        return mse === 0 ? 99 : Math.min(99, 10 * Math.log10(1 / mse));
    }

    // Mean absolute RGB error per column and per row, then the strongest
    // periodic component of each profile. A GPU tiling defect shows up as a
    // period with strength near 1 on one axis; a healthy run has no period
    // worth naming (strength ≈ 0) and badFrac ≈ 0.
    function stripeSignature(a, b) {
        var x = pxOf(a), y = pxOf(b);
        var w = a.width, h = a.height;
        var col = new Float64Array(w), row = new Float64Array(h);
        for (var yy = 0; yy < h; yy++) {
            var rowErr = 0;
            for (var xx = 0; xx < w; xx++) {
                var i = (yy * w + xx) * 4;
                var e = (Math.abs(x[i] - y[i]) + Math.abs(x[i + 1] - y[i + 1]) + Math.abs(x[i + 2] - y[i + 2])) / 765;
                col[xx] += e; rowErr += e;
            }
            row[yy] = rowErr / w;
        }
        for (var c = 0; c < w; c++) col[c] /= h;
        var cx = periodOf(col), cy = periodOf(row);
        // The striped axis is the one whose error PROFILE actually varies:
        // column stripes make every row equally wrong, so the row profile is
        // flat — and the autocorrelation of a flat profile's float noise can
        // still score 0.99 at lag 2. Variance decides the axis; strength then
        // describes only that axis.
        var pick = cx.variance >= cy.variance ? cx : cy;
        var badCols = 0, badRows = 0;
        for (var c2 = 0; c2 < w; c2++) if (col[c2] > BAD_COL_ERR) badCols++;
        for (var r2 = 0; r2 < h; r2++) if (row[r2] > BAD_COL_ERR) badRows++;
        var meanErr = 0; for (var c3 = 0; c3 < w; c3++) meanErr += col[c3]; meanErr /= (w || 1);
        // badFrac is measured ALONG the dominant axis: column stripes make every
        // row slightly wrong, which is not what "fraction of bad rows" means.
        return {
            axis: pick === cx ? 'x' : 'y',
            period: pick.period,
            strength: +pick.strength.toFixed(2),
            badFrac: +(pick === cx ? badCols / (w || 1) : badRows / (h || 1)).toFixed(3),
            meanErr: +meanErr.toFixed(4)
        };
    }

    function periodOf(profile) {
        var n = profile.length, mean = 0, i;
        for (i = 0; i < n; i++) mean += profile[i];
        mean /= (n || 1);
        var c = new Float64Array(n), c0 = 0;
        for (i = 0; i < n; i++) { c[i] = profile[i] - mean; c0 += c[i] * c[i]; }
        var variance = c0 / (n || 1);
        // A profile that barely varies (mean abs error swinging by < 0.005 of
        // full scale) has no stripe worth naming, whatever its autocorrelation.
        if (variance <= 2.5e-5) return { period: 0, strength: 0, variance: variance };
        var best = 0, bestLag = 0, maxLag = Math.min(128, Math.floor(n / 2));
        for (var lag = 2; lag <= maxLag; lag++) {
            var s = 0;
            for (i = 0; i + lag < n; i++) s += c[i] * c[i + lag];
            s /= c0;
            if (s > best) { best = s; bestLag = lag; }
        }
        return { period: bestLag, strength: Math.max(0, best), variance: variance };
    }

    // Deterministic textured frame: gradients, a sine field, a checker, and
    // LCG noise — every kernel gets real work, and the answer is known.
    function syntheticFrame(w, h, seed) {
        var data = new Uint8ClampedArray(w * h * 4);
        var s = (seed >>> 0) || 12345;
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
                var noise = (s / 4294967296 - 0.5) * 40;
                var v = 128 + 60 * Math.sin(x / 9.3) * Math.cos(y / 7.1)
                      + 40 * (((Math.floor(x / 23) + Math.floor(y / 19)) & 1) ? 1 : -1) + noise;
                var i = (y * w + x) * 4;
                data[i]     = v + 20 * Math.sin(y / 31);
                data[i + 1] = v;
                data[i + 2] = 255 - v;
                data[i + 3] = 255;
            }
        }
        return (typeof ImageData !== 'undefined') ? new ImageData(data, w, h) : { data: data, width: w, height: h };
    }

    // A frame whose luma barely varies (black fade-in, a title card) cannot
    // witness a wrong kernel: zeros in, zeros out, 99 dB. Callers substitute
    // syntheticFrame() when this is true.
    function isFlat(img, minStd) {
        var x = pxOf(img); var n = 0, sum = 0, sq = 0;
        var step = Math.max(4, Math.floor(x.length / 4 / 20000) * 4);
        for (var i = 0; i < x.length; i += step) {
            var l = 0.299 * x[i] + 0.587 * x[i + 1] + 0.114 * x[i + 2];
            sum += l; sq += l * l; n++;
        }
        var mean = sum / (n || 1), sd = Math.sqrt(Math.max(0, sq / (n || 1) - mean * mean));
        return sd < (minStd || 8);
    }

    function verdictKey(parts) {
        return parts.map(function (p) { return String(p == null ? '' : p).replace(/\|/g, '/').slice(0, 80); }).join('|');
    }

    function store() {
        try { return (typeof localStorage !== 'undefined') ? localStorage : null; } catch (_) { return null; }
    }
    function readAll() {
        var st = store(); if (!st) return {};
        try { var raw = st.getItem(STORE_KEY); var obj = raw ? JSON.parse(raw) : {}; return (obj && typeof obj === 'object') ? obj : {}; }
        catch (_) { return {}; }
    }
    function writeAll(obj) {
        var st = store(); if (!st) return;
        try { st.setItem(STORE_KEY, JSON.stringify(obj)); } catch (_) {}
    }
    function isRejected(key, provider) {
        var all = readAll(); var rec = all[key + '#' + provider];
        if (!rec || typeof rec.ts !== 'number') return null;
        if (Date.now() - rec.ts > TTL_MS) return null;
        return rec;
    }
    function remember(key, provider, why) {
        var all = readAll();
        all[key + '#' + provider] = { ts: Date.now(), why: why || 'verify', psnr: why && why.psnr, period: why && why.period };
        try { unaccept(key, provider); } catch (_) {}
        // keep the map small: drop expired entries
        for (var k in all) if (all[k] && typeof all[k].ts === 'number' && Date.now() - all[k].ts > TTL_MS) delete all[k];
        writeAll(all);
    }
    function forget(key, provider) {
        var all = readAll(); delete all[key + '#' + provider]; writeAll(all);
    }

    // ── A PASSED test, remembered for a while (2026-10-14, PP-OCR) ──────────
    // The first known-answer round on a GPU pays the shader compilation for the
    // test's own shapes, and the run's shapes compile again: measured on PP-OCR
    // (RTX 4060, Chrome) — round 1 ≈ 5 s, round 2 0.2 s, engine open + first page
    // 2.5–4 s longer than without the test, on every honest GPU, every visit. A
    // consumer whose run is ALSO guarded (PP-OCR checks an empty GPU answer against
    // the CPU and watches for silence) may skip the test on a device that passed it
    // recently — the same trade /sheet-music-to-midi makes with its 14-day tempo
    // verdict (omr_ep_tempo_v1). Put the adapter and the browser's major version
    // in the key; any rejection (remember) or unaccept() drops the record. Not for
    // consumers without a run guard: for them the test before every adoption is
    // the only protection.
    var ACCEPT_KEY = 'tmb_ort_ep_accepted';
    var ACCEPT_TTL_MS = 14 * 24 * 3600 * 1000;
    function readAccepted() {
        var st = store(); if (!st) return {};
        try { var raw = st.getItem(ACCEPT_KEY); var obj = raw ? JSON.parse(raw) : {}; return (obj && typeof obj === 'object') ? obj : {}; }
        catch (_) { return {}; }
    }
    function writeAccepted(obj) {
        var st = store(); if (!st) return;
        try { st.setItem(ACCEPT_KEY, JSON.stringify(obj)); } catch (_) {}
    }
    function accept(key, provider) {
        if (isRejected(key, provider)) return;
        var all = readAccepted(), now = Date.now();
        all[key + '#' + provider] = { ts: now };
        for (var k in all) if (!all[k] || typeof all[k].ts !== 'number' || now - all[k].ts > ACCEPT_TTL_MS) delete all[k];
        writeAccepted(all);
    }
    function isAccepted(key, provider, ttlMs) {
        if (isRejected(key, provider)) return false;
        var rec = readAccepted()[key + '#' + provider];
        return !!(rec && typeof rec.ts === 'number' && Date.now() - rec.ts <= (ttlMs || ACCEPT_TTL_MS));
    }
    function unaccept(key, provider) {
        var all = readAccepted(); if (!all[key + '#' + provider]) return;
        delete all[key + '#' + provider]; writeAccepted(all);
    }

    var _adapterPromise = null;
    function adapterIdentity() {
        if (_adapterPromise) return _adapterPromise;
        _adapterPromise = (async function () {
            var out = { vendor: '', architecture: '', device: '', description: '', fallback: false, present: false };
            try {
                if (typeof navigator === 'undefined' || !navigator.gpu) return out;
                var adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
                if (!adapter) return out;
                out.present = true;
                out.fallback = !!adapter.isFallbackAdapter;
                var info = adapter.info;
                if (!info && typeof adapter.requestAdapterInfo === 'function') info = await adapter.requestAdapterInfo();
                if (info) {
                    out.vendor = String(info.vendor || '').slice(0, 40);
                    out.architecture = String(info.architecture || '').slice(0, 40);
                    out.device = String(info.device || '').slice(0, 40);
                    out.description = String(info.description || '').slice(0, 80);
                }
            } catch (_) {}
            return out;
        })();
        return _adapterPromise;
    }

    async function ladder(opts) {
        var providers = (opts.providers || []).slice();
        var verdicts = [];
        var last = null;
        for (var i = 0; i < providers.length; i++) {
            var provider = providers[i];
            var isCpu = provider === 'wasm' || provider === 'cpu';
            var cached = opts.cacheKey ? isRejected(opts.cacheKey, provider) : null;
            if (cached && !isCpu) {
                var skipped = { provider: provider, ok: false, skipped: 'cached-rejection', cachedTs: cached.ts, why: cached.why };
                verdicts.push(skipped); if (opts.onVerdict) try { opts.onVerdict(skipped); } catch (_) {}
                continue;
            }
            var session;
            try { session = await opts.create(provider); }
            catch (err) {
                var failed = { provider: provider, ok: false, error: String(err && err.message || err).slice(0, 160) };
                verdicts.push(failed); if (opts.onVerdict) try { opts.onVerdict(failed); } catch (_) {}
                continue;
            }
            var rounds = isCpu ? 1 : Math.max(1, opts.repeat || 2);
            var v = null;
            for (var r = 1; r <= rounds; r++) {
                try { v = await opts.verify(session, provider, r); }
                catch (err) {
                    // A cancel is the user's, not the provider's: it must neither
                    // blacklist the GPU nor be swallowed into a WASM run.
                    if (err && (err.name === 'AbortError' || /cancel/i.test(String(err && err.message)))) {
                        try { if (opts.release) opts.release(session); else if (session && session.release) session.release(); } catch (_) {}
                        throw err;
                    }
                    v = { ok: false, error: String(err && err.message || err).slice(0, 160) };
                }
                v = v || { ok: false };
                v.provider = provider; v.round = r;
                if (!v.ok) break;
            }
            verdicts.push(v); if (opts.onVerdict) try { opts.onVerdict(v); } catch (_) {}
            if (v.ok) return { session: session, provider: provider, verdict: v, verdicts: verdicts };
            if (isCpu) {
                // The reference itself failed the test: the test is suspect, not
                // the CPU. Run anyway, say so.
                return { session: session, provider: provider, verdict: v, verdicts: verdicts, unverified: true };
            }
            // Only a WRONG ANSWER is remembered. A test that threw (OOM, a lost
            // device, a transient driver hiccup) is a bad day, not a verdict.
            if (opts.cacheKey && !v.error) remember(opts.cacheKey, provider, { psnr: v.psnr, period: v.sig && v.sig.period, kind: 'verify' });
            try { if (opts.release) opts.release(session); else if (session && session.release) session.release(); } catch (_) {}
            last = v;
        }
        var e = new Error('no execution provider passed verification');
        e.code = 'ep_ladder_exhausted'; e.verdicts = verdicts; e.last = last;
        throw e;
    }

    // Flatten a verdict for telemetry — short, numeric where possible.
    function telemetryOf(v, adapter) {
        var o = {
            ep: v && v.provider || null,
            ok: v && v.ok ? 1 : 0,
            psnr: (v && typeof v.psnr === 'number') ? +v.psnr.toFixed(1) : null,
            ms: (v && typeof v.ms === 'number') ? Math.round(v.ms) : null,
            round: v && v.round || null,
            skipped: v && v.skipped || null,
            err: v && v.error ? String(v.error).slice(0, 80) : null
        };
        if (v && v.sig) { o.sig_axis = v.sig.axis; o.sig_period = v.sig.period; o.sig_strength = v.sig.strength; o.sig_bad = v.sig.badFrac; }
        if (adapter) { o.gpu_vendor = adapter.vendor || null; o.gpu_arch = adapter.architecture || null; o.gpu_device = adapter.device || null; o.gpu_desc = adapter.description || null; o.gpu_fallback = adapter.fallback ? 1 : 0; }
        return o;
    }

    global.OrtEpVerify = {
        psnr: psnr,
        stripeSignature: stripeSignature,
        syntheticFrame: syntheticFrame,
        isFlat: isFlat,
        verdictKey: verdictKey,
        isRejected: isRejected,
        remember: remember,
        forget: forget,
        accept: accept,
        isAccepted: isAccepted,
        unaccept: unaccept,
        adapterIdentity: adapterIdentity,
        ladder: ladder,
        telemetryOf: telemetryOf,
        BAD_COL_ERR: BAD_COL_ERR,
        STORE_KEY: STORE_KEY
    };
})(typeof self !== 'undefined' ? self : globalThis);
